import { materialiseFontSize, stageFor } from './stage-dimensions';
import type {
  AspectRatioToken,
  LayerOverride,
  PropagationResult,
  SetLayer,
  VariantLayerGeometry,
  VariantSetDocument,
  VariantState,
} from './types';

import type { OverlayLayer } from '@/lib/media-studio/overlay-types';

/**
 * THE PROPAGATION MATRIX (spec §7.2), as data rather than prose so the tests can
 * assert it exhaustively and so `classifyProperty` cannot drift from the table.
 *
 * A property is in exactly one of these sets. Anything in neither is `unknown`
 * and is REFUSED rather than guessed at — silently routing an unrecognised
 * property to the set document would propagate geometry, and silently routing it
 * per-variant would break set membership. Both are worse than an error.
 */
export const PROPAGATING_PROPERTIES: ReadonlySet<string> = new Set([
  'text',
  'fontScale',
  'fontFamily',
  'fontWeight',
  'isItalic',
  'isUnderline',
  'textTransform',
  'textAlign',
  'verticalAlign',
  'lineHeight',
  'letterSpacing',
  'fill',
  'contrastMode',
  'contrastColor',
  'contrastIntensity',
  'contrastPadding',
  'boxRoundness',
  'boxShapeType',
  'boxFeather',
  'logoUrl',
  'logoVariant',
  'opacity',
  'shapeType',
  'feather',
  'cornerRadius',
  'points',
  'innerRadius',
  'strokeWidth',
  'strokeColor',
  'name',
  'zIndex',
  'visible',
]);

/**
 * Per-ratio by nature. Never propagates, never marks a sibling for review.
 *
 * `fontSize` is here deliberately and is NOT directly writable: it is *derived*
 * from the propagating `fontScale`. The Size control writes `fontScale`.
 */
export const PER_VARIANT_PROPERTIES: ReadonlySet<string> = new Set([
  'x',
  'y',
  'width',
  'height',
  'rotation',
  'scaleX',
  'scaleY',
  'fontSize',
  'focalX',
  'focalY',
  'zoom',
]);

export type PropertyClass = 'propagating' | 'per-variant' | 'unknown';

export function classifyProperty(name: string): PropertyClass {
  if (PROPAGATING_PROPERTIES.has(name)) return 'propagating';
  if (PER_VARIANT_PROPERTIES.has(name)) return 'per-variant';
  return 'unknown';
}

/** Split a mixed patch. Throws on an unknown key rather than guessing its home. */
export function splitPatch(patch: Record<string, unknown>): {
  propagating: Record<string, unknown>;
  perVariant: Record<string, unknown>;
} {
  const propagating: Record<string, unknown> = {};
  const perVariant: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    const kind = classifyProperty(key);
    if (kind === 'propagating') propagating[key] = value;
    else if (kind === 'per-variant') perVariant[key] = value;
    else throw new Error(`Unknown layer property "${key}" — add it to the propagation matrix.`);
  }
  return { propagating, perVariant };
}

const DEFAULT_TEXT_WIDTH_FRACTION = 0.84;
const DEFAULT_MARGIN_FRACTION = 0.08;
const DEFAULT_TEXT_Y_FRACTION = 0.72;
const DEFAULT_MARK_FRACTION = 0.14;

/**
 * Initialise geometry for a layer in a variant that has none.
 *
 * Seeding is NOT propagation. It runs once, when a ratio is added to the set or
 * a layer is added to it. A later geometry edit on the source variant does not
 * flow anywhere — that is what §7.2 forbids, and it is covered by test.
 */
export function seedGeometry(
  layer: SetLayer,
  ratio: AspectRatioToken,
  source?: { geometry: VariantLayerGeometry; ratio: AspectRatioToken },
): VariantLayerGeometry {
  const stage = stageFor(ratio);
  if (source) {
    const from = stageFor(source.ratio);
    const sx = stage.width / from.width;
    const sy = stage.height / from.height;
    return {
      x: source.geometry.x * sx,
      y: source.geometry.y * sy,
      width: source.geometry.width * sx,
      height: source.geometry.height === undefined ? undefined : source.geometry.height * sy,
      rotation: source.geometry.rotation,
      scaleX: source.geometry.scaleX,
      scaleY: source.geometry.scaleY,
    };
  }
  if (layer.kind === 'text') {
    const y = Math.round(stage.height * DEFAULT_TEXT_Y_FRACTION);
    return {
      x: Math.round(stage.width * DEFAULT_MARGIN_FRACTION),
      y,
      width: Math.round(stage.width * DEFAULT_TEXT_WIDTH_FRACTION),
      // The band a text layer can align within (KonvaTextNode / overlay-svg
      // both read `element.height` as "the box `verticalAlign` measures
      // top/middle/bottom against", NOT the glyph-measured height of the
      // text). Default it to the space from the default y down to a bottom
      // margin that mirrors DEFAULT_MARGIN_FRACTION — the same breathing
      // room the left/right margins already give — so a fresh "bottom"
      // layer lands just inside the artboard edge, not flush against it.
      height: Math.round(stage.height * (1 - DEFAULT_MARGIN_FRACTION) - y),
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
    };
  }
  const size = Math.round(stage.width * DEFAULT_MARK_FRACTION);
  return {
    x: Math.round(stage.width * DEFAULT_MARGIN_FRACTION),
    y: Math.round(stage.height * DEFAULT_MARGIN_FRACTION),
    width: size,
    height: size,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
  };
}

/** Set value, then the variant's override on top. Overrides always win (§7.4). */
export function resolveLayerProps(layer: SetLayer, override: LayerOverride | undefined): SetLayer {
  return (override ? { ...layer, ...override } : layer) as SetLayer;
}

/**
 * Produce what Konva and the server compositor actually render for one variant.
 * `fontSize` appears only here — it is re-derived on every materialisation and is
 * never stored on the set.
 */
export function materialiseLayer(layer: SetLayer, variant: VariantState): OverlayLayer | null {
  const resolved = resolveLayerProps(layer, variant.overrides[layer.id]);
  if (!resolved.visible) return null;
  const stage = stageFor(variant.aspectRatio);
  const geometry = variant.geometry[layer.id] ?? seedGeometry(resolved, variant.aspectRatio);

  const base = {
    id: layer.id,
    stageWidth: stage.width,
    x: geometry.x,
    y: geometry.y,
    width: geometry.width,
    rotation: geometry.rotation,
    scaleX: geometry.scaleX,
    scaleY: geometry.scaleY,
    zIndex: resolved.zIndex,
  };

  if (resolved.kind === 'text') {
    return {
      ...base,
      kind: 'text',
      height: geometry.height,
      text: resolved.text,
      fontSize: materialiseFontSize(resolved.fontScale, variant.aspectRatio),
      fontFamily: resolved.fontFamily,
      fontWeight: resolved.fontWeight,
      isItalic: resolved.isItalic,
      isUnderline: resolved.isUnderline,
      lineHeight: resolved.lineHeight,
      letterSpacing: resolved.letterSpacing,
      textAlign: resolved.textAlign,
      verticalAlign: resolved.verticalAlign,
      textTransform: resolved.textTransform,
      fill: resolved.fill,
      contrastMode: resolved.contrastMode,
      contrastColor: resolved.contrastColor,
      contrastIntensity: resolved.contrastIntensity,
      contrastPadding: resolved.contrastPadding,
      boxRoundness: resolved.boxRoundness,
      boxShapeType: resolved.boxShapeType,
      boxFeather: resolved.boxFeather,
    } as OverlayLayer;
  }

  if (resolved.kind === 'logo') {
    return {
      ...base,
      kind: 'logo',
      height: geometry.height ?? geometry.width,
      logoUrl: resolved.logoUrl,
      logoVariant: resolved.logoVariant,
      opacity: resolved.opacity,
    } as OverlayLayer;
  }

  return {
    ...base,
    kind: 'shape',
    height: geometry.height ?? geometry.width,
    shapeType: resolved.shapeType,
    fill: resolved.fill,
    opacity: resolved.opacity,
    feather: resolved.feather,
    cornerRadius: resolved.cornerRadius,
    points: resolved.points,
    innerRadius: resolved.innerRadius,
    strokeWidth: resolved.strokeWidth,
    strokeColor: resolved.strokeColor,
  } as OverlayLayer;
}

/** The full render list for one variant, in stacking order. */
export function materialiseVariant(doc: VariantSetDocument, variantId: string): OverlayLayer[] {
  const variant = doc.variants.find((v) => v.id === variantId);
  if (!variant) return [];
  return [...doc.layers]
    .sort((a, b) => a.zIndex - b.zIndex)
    .map((layer) => materialiseLayer(layer, variant))
    .filter((layer): layer is OverlayLayer => layer !== null);
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Apply a propagating patch to the set document and work out what it did to each
 * variant.
 *
 * The three rules that make this the crux of the whole feature:
 *
 *  1. **Overrides survive.** A variant that has overridden a patched property
 *     keeps its value. It is reported in `keptOverrides` so the UI can name it
 *     ("9:16 kept its override") instead of silently discarding a manual fix —
 *     AdCreative.ai's documented hole.
 *  2. **Siblings are marked `needsReview`.** Every variant whose materialised
 *     output actually changed, other than the one the user was looking at, is
 *     flagged. A variant that kept an override on every patched property was not
 *     touched and is not flagged — flagging it would be a false blocker on the
 *     Ready gate, which is the one gate that must never cry wolf.
 *  3. **The source variant is never flagged.** The user is looking at it.
 */
export function propagateSetLayerPatch(
  doc: VariantSetDocument,
  layerId: string,
  patch: LayerOverride & Partial<Pick<SetLayer, 'name' | 'zIndex' | 'visible'>>,
  sourceVariantId: string | null,
): { doc: VariantSetDocument; result: PropagationResult } {
  const index = doc.layers.findIndex((l) => l.id === layerId);
  if (index === -1) {
    return {
      doc,
      result: { changedVariantIds: [], keptOverrides: [], markedForReviewIds: [] },
    };
  }

  const previous = doc.layers[index];
  const patchedKeys = Object.keys(patch).filter(
    (key) =>
      !shallowEqual(
        (previous as unknown as Record<string, unknown>)[key],
        (patch as Record<string, unknown>)[key],
      ),
  );
  if (patchedKeys.length === 0) {
    return {
      doc,
      result: { changedVariantIds: [], keptOverrides: [], markedForReviewIds: [] },
    };
  }

  const nextLayer = { ...previous, ...patch } as SetLayer;
  const layers = [...doc.layers];
  layers[index] = nextLayer;

  const changedVariantIds: string[] = [];
  const keptOverrides: PropagationResult['keptOverrides'] = [];
  const markedForReviewIds: string[] = [];

  const variants = doc.variants.map((variant) => {
    const override = variant.overrides[layerId];
    const kept = override ? patchedKeys.filter((key) => key in override) : [];
    const effective = patchedKeys.filter((key) => !kept.includes(key));

    if (kept.length > 0) {
      keptOverrides.push({
        variantId: variant.id,
        aspectRatio: variant.aspectRatio,
        properties: kept,
      });
    }
    if (effective.length === 0) return variant;

    changedVariantIds.push(variant.id);
    if (variant.id === sourceVariantId || variant.needsReview) return variant;
    markedForReviewIds.push(variant.id);
    return { ...variant, needsReview: true };
  });

  return {
    doc: { ...doc, layers, variants },
    result: { changedVariantIds, keptOverrides, markedForReviewIds },
  };
}

/** Human-readable §7.4 toast text: "Colour updated on 2 sizes · 9:16 kept its override." */
export function describePropagation(result: PropagationResult, label: string): string {
  const count = result.changedVariantIds.length;
  const head = count === 1 ? `${label} updated on 1 size` : `${label} updated on ${count} sizes`;
  if (result.keptOverrides.length === 0) return `${head}.`;
  const kept = result.keptOverrides.map((k) => k.aspectRatio).join(', ');
  const verb = result.keptOverrides.length === 1 ? 'kept its override' : 'kept their overrides';
  return `${head} · ${kept} ${verb}.`;
}
